import { CoreButtonView, type TCoreButtonViewProps } from '@core/widgets';
import { AssetsSvgMap, TButtonData, TMargin, TWidget } from '@libs/types';
import { Glass, type GlassOptics } from '@samasante/liquid-glass';
import React, { MouseEvent } from 'react';
import { SemanticColor, semanticColors } from '../../theme/colors';
import { TButtonOption } from '../../types/options';
import { TVisualViewProps, VisualView } from '../Image/VisualView';
import styles from './styles/buttonCircleGlass.module.css';

/**
 * The optical look, fixed at module scope on purpose.
 *
 * <Glass> regenerates its displacement map whenever a shape-affecting optic
 * (depth / curvature / bend / sheen / glow / splay) changes, so animating optics
 * per interaction would rasterize a new map on every hover. Hover and press are
 * expressed through the CSS tint instead, which keeps the map generated exactly
 * once per button size.
 *
 * `mapSize` is dropped from the library default of 512 because these buttons are
 * 28-56px: a 128px map is already well above the on-screen resolution, and it cuts
 * the generation cost by ~16x, which matters when several circles share a screen.
 */
const GLASS_OPTICS: Partial<GlassOptics> = {
    mapSize: 128,
    frost: 6,
    saturate: 1.2,
    strength: 0.06,
    depth: 0.5,
    curvature: 0.35,
    // A circle reads as glass mostly through its rim, so the meniscus is pushed
    // slightly above the library default while the body dome stays gentle.
    bend: 0.5,
    bendWidth: 0.18,
    dispersion: 0.3,
    sheen: 0.35,
    sheenWidth: 2,
    glow: 0.12,
    brightness: 0.06,
};

export type TButtonCircleGlassViewProps = {
    uid?: string;
    /** Outer diameter in px. Resolved by ButtonCircleView from size/icon dimensions. */
    circleSize: number;
    iconName?: string;
    iconWidth: number;
    iconHeight: number;
    iconSize?: TVisualViewProps['size'];
    iconHierarchy?: TVisualViewProps['iconHierarchy'];
    iconColor?: TVisualViewProps['iconColor'];
    backgroundColor?: TVisualViewProps['backgroundColor'];
    label?: string;
    hierarchy?: TButtonOption['hierarchy'];
    margin?: TMargin;
    isDisabled?: boolean;
    isHide?: boolean;
    parentStyleNames?: string[];
    onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
    widget?: TWidget<TButtonData, TButtonOption>;
};

export const ButtonCircleGlassView = ({
    uid,
    circleSize,
    iconName,
    iconWidth,
    iconHeight,
    iconSize,
    iconHierarchy,
    iconColor,
    backgroundColor,
    label,
    margin,
    isDisabled,
    isHide,
    parentStyleNames,
    onClick,
    widget,
}: TButtonCircleGlassViewProps) => {
    const icon = AssetsSvgMap[iconName as keyof typeof AssetsSvgMap] ?? AssetsSvgMap.ic_x;

    const resolvedWidget: TCoreButtonViewProps['widget'] = (widget ?? { uid: '', data: {}, options: {} }) as TCoreButtonViewProps['widget'];

    return (
        <CoreButtonView
            uid={uid}
            widget={resolvedWidget}
            onClick={onClick ?? (() => undefined)}
            isDisabled={isDisabled}
            isHide={isHide}
            parentStyleNames={parentStyleNames}
            text={label}
            styleProps={{
                main: semanticColors[SemanticColor.FillTertiaryOnDark],
                borderRadius: circleSize / 2,
                margin,
                hovered: {
                    main: semanticColors[SemanticColor.FillTertiaryOnDarkHovered],
                },
                pressed: {
                    main: semanticColors[SemanticColor.FillTertiaryOnDarkPressed],
                },
                disabled: {
                    main: semanticColors[SemanticColor.FillTertiaryOnDarkDisabled],
                },
            }}
        >
            <Glass width={circleSize} height={circleSize} radius={circleSize / 2} optics={GLASS_OPTICS}>
                <span className={styles.content}>
                    <VisualView
                        isDisabled={isDisabled}
                        parentStyleNames={parentStyleNames}
                        styleName="buttonCircleIcon"
                        defaultIconName={AssetsSvgMap.ic_x}
                        size={iconSize}
                        iconWidth={iconWidth}
                        iconHeight={iconHeight}
                        iconName={icon}
                        iconHierarchy={iconHierarchy}
                        iconVariant="simple"
                        iconColor={iconColor ? iconColor : semanticColors[SemanticColor.IconPrimaryOnDarkFill]}
                        backgroundColor={backgroundColor}
                    />
                </span>
            </Glass>
        </CoreButtonView>
    );
};
